import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BrandMark } from './BrandMark';
import { MobileNav } from './MobileNav';
import { NotificationBell } from './NotificationBell';
import { LogoutButton } from './LogoutButton';
import { TrialBar } from './overlays/TrialBar';
import { OnboardingOverlay } from './overlays/OnboardingOverlay';
import { PostActivationCardAsk } from './overlays/PostActivationCardAsk';
import { TourController } from './overlays/TourController';
import { useSessionToken, useMe, usePlan, planLabel } from '../hooks';
import { api } from '../lib/api';
import { daypartGreeting } from '../lib/display';
import { LOCKED_THEME } from '../theme/lockedTheme';
import '../styles/shell.css';

/* Inline icon helper (paths ported from the design's app shell SVGs). */
const I = (d: ReactNode) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9}>{d}</svg>
);

type NavDef = {
  to: string; label: string; icon: ReactNode; badge?: string;
  title: string; sub: string;
  /* Pass C tour hook: the interactive tour highlights the live nav item
     carrying this data-tour key (see TourController + overlays.css). */
  tour?: string;
};

/* Each nav section owns one hue, and it is the same hue its screens speak.
   The mapping, the reasoning, and why RED is deliberately NOT here are locked
   in styles/tokens.css ("THE NAV SECTION MAPPING"):
     MAIN     gold  = EARNED / REPUTATION   (reviews, ratings, the score)
     AUTOMATE blue  = OUTBOUND / THE ASK    (templates, automations, sends)
     GROW     green = DELIVERED / CONNECTED (integrations, listings, reports)
   The utility group in side__foot stays .hue-none: it is chrome, not work. */
const SECTIONS: { sec: string; hue: string; items: NavDef[] }[] = [
  {
    sec: 'Main',
    hue: 'hue-gold',
    items: [
      { to: '/dashboard', label: 'Dashboard', title: 'Dashboard', sub: "Good morning - here's how your business is doing.", icon: I(<path d="M4 13h7V4H4zM13 20h7v-9h-7zM13 4v4h7V4zM4 20h7v-3H4z" strokeLinejoin="round" />) },
      { to: '/reviews', label: 'Reviews', title: 'Reviews', sub: 'Approve AI-drafted replies and post across every platform.', tour: 'reviews', icon: I(<path d="M21 11.5a8.4 8.4 0 01-9 8 8.4 8.4 0 01-3.5-.7L3 20l1.3-4A8.4 8.4 0 0112 3.5a8.4 8.4 0 019 8z" strokeLinejoin="round" />) },
      { to: '/requests', label: 'Requests', title: 'Requests', sub: 'Send review asks by email and track who responded.', icon: I(<><path d="M4 5h16v11H7l-3 3z" strokeLinejoin="round" /><path d="M8 9h8M8 12h5" strokeLinecap="round" /></>) },
      { to: '/customers', label: 'Customers', title: 'Customers', sub: 'Your contact list and one-click imports.', tour: 'customers', icon: I(<><circle cx="9" cy="8" r="3.2" /><path d="M3.5 19a5.5 5.5 0 0111 0M16 6.5a3 3 0 010 6M21 19a5.2 5.2 0 00-4-5" strokeLinecap="round" strokeLinejoin="round" /></>) },
      /* Text opt-ins (Penn Condition 1, 2026-07-29). Sits next to Customers
         because it answers a question the contact list cannot: a phone number
         is not permission to text. */
      { to: '/text-opt-ins', label: 'Text opt-ins', title: 'Text opt-ins', sub: 'Who asked for texts, who confirmed, and who cannot be texted yet.', icon: I(<><path d="M4 5h16v11H7l-3 3z" strokeLinejoin="round" /><path d="M9 10.5l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" /></>) },
    ],
  },
  {
    sec: 'Automate',
    hue: 'hue-blue',
    items: [
      { to: '/templates', label: 'Templates', title: 'Templates', sub: 'Craft and A/B-test your review request messages.', tour: 'template', icon: I(<><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 8h8M8 12h8M8 16h5" strokeLinecap="round" /></>) },
      { to: '/automations', label: 'Automations', title: 'Automations', sub: 'Set it and forget it - rules that run your reputation.', tour: 'automate', icon: I(<path d="M13 2L3 14h7l-1 8 10-12h-7z" strokeLinejoin="round" />) },
      { to: '/feedback-gate', label: 'Private feedback', title: 'Private feedback', sub: 'Offer a private channel to every customer, alongside the public review link.', icon: I(<><path d="M12 2l8 3v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V5z" strokeLinejoin="round" /><path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" /></>) },
    ],
  },
  {
    sec: 'Grow',
    hue: 'hue-green',
    items: [
      { to: '/integrations', label: 'Integrations', title: 'Integrations', sub: 'Connect Google, Zapier & the tools that send you customers.', tour: 'google', icon: I(<><path d="M9 7V4.5a2.5 2.5 0 015 0V7M15 17v2.5a2.5 2.5 0 01-5 0V17" strokeLinecap="round" /><rect x="4" y="7" width="16" height="10" rx="2" /><path d="M4 12h3M17 12h3" strokeLinecap="round" /></>) },
      { to: '/listings', label: 'Listings', title: 'Listings', sub: 'Make sure your name, address & phone match everywhere customers find you.', icon: I(<><path d="M12 21s-6.5-5.5-6.5-10.5a6.5 6.5 0 0113 0C18.5 15.5 12 21 12 21z" strokeLinejoin="round" /><circle cx="12" cy="10.5" r="2.4" /></>) },
      { to: '/widget', label: 'Site Widget', title: 'Site Widget', sub: 'Configure the review showcase you embed on your site.', tour: 'widget', icon: I(<><rect x="3.5" y="4.5" width="17" height="15" rx="2" /><path d="M3.5 9h17" strokeLinecap="round" /></>) },
      /* Collection nav PAUSED 2026-08-07 (Steve) - QR Collection, may return.
         The screen shipped without a QR generator, so it is withdrawn from the
         app. Same pattern as Social Posting below: hidden, not deleted; the
         /collection route now redirects to /dashboard (see App.tsx). Restore
         this line to bring the nav item back. */
      // { to: '/collection', label: 'Collection', title: 'Collection assets', sub: 'QR codes & email tools to collect reviews everywhere.', icon: I(<><rect x="4" y="4" width="7" height="7" rx="1" /><rect x="13" y="4" width="7" height="7" rx="1" /><rect x="4" y="13" width="7" height="7" rx="1" /><path d="M14 14h6M14 17h6M14 20h3" strokeLinecap="round" /></>) },
      /* Social Posting nav hidden (2026-07-06): publishing to Meta is not live
         (gated on Meta App Review), so it is not surfaced as a working feature.
         The /social route still exists; re-add this entry when publishing works.
         Flagged for Steve's call. */
      // { to: '/social', label: 'Social Posting', title: 'Social Posting', sub: 'Turn your best reviews into Facebook & Instagram posts.', icon: I(<><circle cx="18" cy="5" r="2.4" /><circle cx="6" cy="12" r="2.4" /><circle cx="18" cy="19" r="2.4" /><path d="M8.1 10.8l7.8-4.4M8.1 13.2l7.8 4.4" strokeLinecap="round" /></>) },
      { to: '/google-posts', label: 'Google Posts', title: 'Google Posts', sub: 'Post updates, offers and events to your Google profile.', icon: I(<><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M8 13h8M8 16h5" strokeLinecap="round" /></>) },
      { to: '/competitors', label: 'Competitors', title: 'Competitors', sub: 'Track your rank and out-pace the competition.', icon: I(<path d="M4 20V4M4 20h16M8 16v-5M12 16V8M16 16v-7" strokeLinecap="round" strokeLinejoin="round" />) },
      { to: '/reports', label: 'Reports', title: 'Reports', sub: 'Trends, benchmarks and exports over any range.', icon: I(<path d="M6 20V10M12 20V4M18 20v-7" strokeLinecap="round" strokeLinejoin="round" />) },
      { to: '/feedback', label: 'Feature requests', title: 'Feature requests', sub: 'Tell us what to build next, and upvote the ideas you want most.', icon: I(<><path d="M9 18h6M10 21h4" strokeLinecap="round" /><path d="M12 3a6 6 0 0 0-3.5 10.9c.6.4.9 1 .9 1.6v.5h5.2v-.5c0-.6.3-1.2.9-1.6A6 6 0 0 0 12 3z" strokeLinejoin="round" /></>) },
    ],
  },
];

/* Sidebar-footer destinations (below the APPEARANCE toggle), ported from the
   prototype's rail footer: Tutorials, then Settings. Tutorials is a real nav
   destination in the build (new /tutorials route). */
const TUTORIALS: NavDef = { to: '/tutorials', label: 'Tutorials', title: 'Tutorials', sub: 'Short guides and the interactive product tour.', icon: I(<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2zM22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" strokeLinejoin="round" />) };
const SETTINGS: NavDef = { to: '/settings', label: 'Settings', title: 'Settings', sub: 'Business profile, brand voice, integrations, team & billing.', tour: 'profile', icon: I(<><circle cx="12" cy="12" r="3.2" /><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6L17 7M7 17l-1.4 1.4" strokeLinecap="round" /></>) };

const ALL = [...SECTIONS.flatMap((s) => s.items), TUTORIALS, SETTINGS];

export function AppShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const active = ALL.find((n) => pathname.startsWith(n.to)) ?? ALL[0];
  // U12: the Dashboard sub greeting must reflect the real time of day, not a
  // hard-coded "Good morning". Recompute the greeting prefix for that one tab.
  const activeSub =
    active.to === '/dashboard'
      ? `${daypartGreeting()} - here's how your business is doing.`
      : active.sub;
  // V12: the prototype showed a FAKE 850ms skeleton on every mount -- pure
  // added latency with no real loading behind it. Removed. Each screen has its
  // own honest loading state for data that is genuinely still fetching, so the
  // shell now paints immediately.
  // The topbar "Request reviews" button navigates to the single Requests page
  // (the old popup composer is retired -- one request screen, no duplicate UI).
  // Global search: there is no cross-entity search backend, so the honest
  // behavior is to jump to the Customers list pre-filtered by the term
  // (Customers reads ?q= and filters its already-loaded rows client-side).
  const [search, setSearch] = useState('');
  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    const q = search.trim();
    navigate(q ? `/customers?q=${encodeURIComponent(q)}` : '/customers');
  }
  // Mount the Supabase->rr_ token exchange once at the shell so every screen
  // (and the always-visible bell) has a data-plane token. Module-deduped, so
  // mounting it here plus on a data screen is still one network call.
  useSessionToken();
  const me = useMe();
  const plan = usePlan();
  const bizName = me.data?.business_name ?? 'Your business';
  // M2: one source of truth for the plan label. The sidebar chip now reads the
  // authoritative billing-plan status (same as the Billing panel), so it can
  // never say "pro plan" while Billing says "no plan".
  const tier = planLabel(plan.data);

  // Operator-only entry back to /agency. Same query key as routes/Agency.tsx
  // and routes/PostAuthLanding.tsx, so the cache is shared (no extra request).
  // FAIL CLOSED: only is_admin === true reveals the link. A non-admin, a
  // 401/403, any error, or the still-loading state all render nothing -- a
  // normal client must never see this.
  const adminMeQ = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: ({ signal }) => api.getAdminMe(signal),
    retry: false,
    staleTime: 5 * 60_000,
  });
  const isOperator = adminMeQ.data?.is_admin === true;

  const item = (n: NavDef) => (
    <NavLink key={n.to} to={n.to} data-tour={n.tour} className={({ isActive }) => 'navitem' + (isActive ? ' is-active' : '')}>
      {n.icon} {n.label}
      {n.badge && <span className="badge">{n.badge}</span>}
    </NavLink>
  );

  /* APPEARANCE IS LOCKED (Steve, 2026-08-06): outdoor high-contrast is the one
     and only look. data-theme below is a CONSTANT marker that the outdoor rules
     in theme/theme.css hang off, not a setting -- there is no switcher, no
     context and no stored preference that can change it. */
  return (
    <div className="app" data-theme={LOCKED_THEME}>
      <aside className="side">
        <BrandMark />
        <nav className="nav">
          {SECTIONS.map((s) => (
            /* The hue class is what turns the whole group coloured: the rail,
               the section label, and every item's icon all read it through
               the shared --sec-* tokens. */
            <div key={s.sec} className={`nav__group ${s.hue}`}>
              <div className="sec">{s.sec}</div>
              {s.items.map(item)}
            </div>
          ))}
        </nav>
        {/* Utility group: Tutorials / Operator console / Settings / Log out.
            .hue-none on purpose -- see tokens.css. This is chrome, not one of
            the product's three working areas, and the only unspent hue is red,
            which is reserved for "needs attention" and would stop meaning
            anything if it lived permanently in the rail. */}
        <div className="side__foot hue-none">
          {/* The Appearance toggle that used to sit here is RETIRED (Steve,
              2026-08-06). The app is outdoor high-contrast, always. */}
          {/* Tutorials nav HIDDEN (Steve, 2026-08-06): the guides are stale.
              Today's UI pass retired the appearance switcher, colour-coded the
              whole nav by section, and reworked Integrations, so a walkthrough
              that points at an Appearance setting or a Social Posting screen
              now tells an owner to click something that is not there. That is
              worse than no tutorial.
              HIDDEN, NOT DELETED -- same pattern as Social Posting above. The
              /tutorials route stays mounted so an existing bookmark does not
              404, and TUTORIALS stays in ALL (below) so a deep link still gets
              the right topbar title. Re-add this line once the guides are
              refreshed against the 2026-08-06 UI.
              NOTE: the on-demand product tour is launched ONLY from the
              Tutorials screen, so hiding this hides that tour too. The
              POST-CHUTE setup tour is unaffected -- it is dispatched from
              Welcome (rhody:start-setup-tour) and its four steps target
              profile / widget / customers / request, none of which are here. */}
          {/* {item(TUTORIALS)} */}
          {/* Operator console -- visible ONLY to platform operators (is_admin).
              One-click route back to /agency from any consumer screen. Hidden
              for normal clients and on any error/loading (fail closed). */}
          {isOperator && (
            <NavLink
              to="/agency"
              className={({ isActive }) => 'navitem' + (isActive ? ' is-active' : '')}
            >
              {I(<><rect x="3.5" y="4.5" width="17" height="13" rx="2" /><path d="M8 20h8M12 17.5V20" strokeLinecap="round" strokeLinejoin="round" /></>)}
              Operator console
            </NavLink>
          )}
          {item(SETTINGS)}
          {/* Log out -- clears the Supabase session and returns to /auth.
              Available on every client dashboard screen. */}
          <LogoutButton variant="side" />
          <div className="bizcard" style={{ marginTop: 6 }}>
            <span className="ava">{(bizName[0] || 'R').toUpperCase()}</span>
            <div className="t"><b>{bizName}</b><span>{tier}</span></div>
          </div>
        </div>
      </aside>

      <main className="main">
        {/* Trial countdown bar -- pinned above the topbar; shows only for a
            real non-pro trial with a real trial-end timestamp, else hides. */}
        <TrialBar />
        <header className="topbar">
          <div>
            <h1>{active.title}</h1>
            <div className="sub">{activeSub}</div>
          </div>
          <form className="search" onSubmit={submitSearch}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" strokeLinecap="round" /></svg>
            <input
              placeholder="Search customers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </form>
          <NotificationBell />
          <button className="btn btn--primary" data-tour="request" onClick={() => navigate('/requests')}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}><path d="M12 5v14M5 12h14" strokeLinecap="round" /></svg> Request reviews</button>
        </header>
        <section className="view"><Outlet /></section>
      </main>

      {/* Mobile bottom tab bar -- CSS-hidden above 840px, where the sidebar
          (above) takes over. Lets phones navigate the essential screens. */}
      <MobileNav />

      {/* Pass C overlays / flows -- all mounted at the shell so they cover any
          screen. Each is real-state gated inside its own component:
            - OnboardingOverlay: first-run for brand-new accounts + on-demand
              via 'rhody:open-onboarding' (portal to <body>).
            - TourController: listens for 'rhody:start-tour' from Tutorials and
              walks the live shell nav via data-tour hooks.
          (V12: the fake 850ms SkeletonGate was removed -- no real load behind it.) */}
      <OnboardingOverlay />
      {/* Reverse-trial card ask (Phase 5, spec §7): the post-activation "you got
          value, start your plan" prompt. Non-blocking; real-state gated inside
          (reverse-trial arm + activated + no active paid sub). Portals to
          <body>, so its position in the tree does not affect layout. */}
      <PostActivationCardAsk />
      <TourController />
    </div>
  );
}
